/**
 * PATH: packages/job-hub/src/jobs/tabs/JobQaSetupCard.tsx
 * FILE_ID: JOBHUB-JOBS-QA-SETUP-CARD-001
 * PURPOSE: "Set up QA from quotes" card — multi-select quote picker (totals /
 *          status / validity + open-to-review), classification panel, combine
 *          vs separate mode, scope name and the Create button.
 * WHY:     Extracted VERBATIM from JobQaTab.tsx (1,127 lines) to bring the
 *          frame under the 500-line ceiling. Purely presentational: all state
 *          and the create mutation stay in the frame.
 * LANDMINE: The quote ROW toggles selection; the quote-number link and the
 *          "Open" button must stopPropagation or a review click also ticks
 *          the checkbox (operator complaint 2026-06-17).
 * RELATED:
 * - JOBHUB-JOBS-TAB-QA-001 (the frame that owns the state)
 * - jobQaSetup.pin.test.ts (pins this flow)
 */
import {
  AlertCircle,
  ChevronDown,
  ClipboardCheck,
  ExternalLink,
  Layers,
  Loader2,
  SplitSquareHorizontal,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { cn, formatCurrency } from '@quotech/tenant-shared/lib/utils'
import { ResizeHandle, StatusBadge, useIsMdUp, usePersistentBool, usePersistentSize } from '@quotech/ui'
import {
  QA_SETUP_COLLAPSED_KEY,
  readQaSetupCollapsedStored,
  resolveQaSetupCollapsed,
} from './job-qa/qaSetupCollapsed'
import { QaPhoneSetupFlow } from './job-qa/phone/setup/QaPhoneSetupFlow'
import type { QuoteRow } from '../jobDetailTypes'
import { openQuotePopup, viewQuoteUrl } from './job-qa/openQuotePopup'
import { QuoteIngestErrorNotice } from './QuoteIngestErrorNotice'
import type { QaClassification } from '../qa/classification'
import type { suggestClassification } from '../qa/classification'
import type { QaWriteTarget } from '../qa/qaTypes'

/** How selected quote sheets become inspections. */
export type QaCreateMode = 'combine' | 'separate'
export type { QaWriteTarget }

export function JobQaSetupCard({
  quotes,
  quotesLoading,
  quotesError = false,
  onRetryQuotes,
  onOpenQuote,
  selectedSheetIds,
  onToggleSheet,
  onRemoveQuote,
  classification,
  suggestion,
  onClassificationChange,
  createMode,
  onCreateModeChange,
  writeTarget = 'create',
  onWriteTargetChange,
  showWriteTarget = false,
  includeQuoteDerivedItems = true,
  onIncludeQuoteDerivedItemsChange,
  scopeName,
  onScopeNameChange,
  onCreate,
  onCombine,
  createPending,
  createProgress,
  createNotice,
  genError,
  previewPanel,
  onRetryImport,
  retryingImportId,
  retryImportError,
  addQuotes,
  hasInspections = false,
  embedded = false,
  fillPage = false,
}: {
  quotes: QuoteRow[]
  quotesLoading: boolean
  quotesError?: boolean
  onRetryQuotes?: () => void
  onOpenQuote?: (q: QuoteRow) => void
  selectedSheetIds: number[]
  onToggleSheet: (id: number) => void
  onRemoveQuote?: (id: number) => void
  classification: QaClassification
  suggestion: ReturnType<typeof suggestClassification>
  onClassificationChange: (next: QaClassification) => void
  createMode: QaCreateMode
  onCreateModeChange: (mode: QaCreateMode) => void
  writeTarget?: QaWriteTarget
  onWriteTargetChange?: (target: QaWriteTarget) => void
  showWriteTarget?: boolean
  includeQuoteDerivedItems?: boolean
  onIncludeQuoteDerivedItemsChange?: (value: boolean) => void
  scopeName: string
  onScopeNameChange: (name: string) => void
  onCreate: () => void
  /** Combine asks where the QA goes. Separate creates immediately. */
  onCombine?: () => void
  createPending: boolean
  createProgress: { current: number; total: number } | null
  createNotice?: string | null
  genError: string | null
  previewPanel?: ReactNode
  /** Re-run a failed quote import. Absent = the notice renders read-only. */
  onRetryImport?: (quoteSheetId: number) => void
  retryingImportId?: number | null
  /** Keyed by row: the spinner id clears on settle, so the message needs its
   *  own owner or it would render against whichever row was last touched. */
  retryImportError?: { id: number; message: string } | null
  addQuotes?: ReactNode
  /** When a QA list already exists, start collapsed unless the user chose. */
  hasInspections?: boolean
  /** Strip owns the chrome. Render the quote body only. */
  embedded?: boolean
  /** Phone Set Up page. Drops the 45vh cap. Desktop callers omit it. */
  fillPage?: boolean
}) {
  const isMdUp = useIsMdUp()
  const listedQuotes = quotes.filter((row) => !row.hidden_from_qa)
  const [collapsed, setCollapsed] = usePersistentBool(
    QA_SETUP_COLLAPSED_KEY,
    Boolean(hasInspections),
  )
  const [maxH, setMaxH] = usePersistentSize('qa.setup.maxh', 460)
  const collapsedNow = resolveQaSetupCollapsed(
    hasInspections,
    readQaSetupCollapsedStored(),
    collapsed,
  )
  const open = embedded || !collapsedNow || Boolean(previewPanel)
  if (!isMdUp) {
    return (
      <QaPhoneSetupFlow
        embedded={embedded}
        fillPage={fillPage}
        quotes={listedQuotes}
        quotesLoading={quotesLoading}
        quotesError={quotesError}
        onRetryQuotes={onRetryQuotes}
        onOpenQuote={onOpenQuote}
        selectedSheetIds={selectedSheetIds}
        onToggleSheet={onToggleSheet}
        onRemoveQuote={onRemoveQuote}
        classification={classification}
        suggestion={suggestion}
        onClassificationChange={onClassificationChange}
        createMode={createMode}
        onCreateModeChange={onCreateModeChange}
        writeTarget={writeTarget}
        onWriteTargetChange={onWriteTargetChange}
        showWriteTarget={showWriteTarget}
        includeQuoteDerivedItems={includeQuoteDerivedItems}
        onIncludeQuoteDerivedItemsChange={onIncludeQuoteDerivedItemsChange}
        scopeName={scopeName}
        onScopeNameChange={onScopeNameChange}
        onCreate={onCreate}
        createPending={createPending}
        createProgress={createProgress}
        createNotice={createNotice}
        genError={genError}
        previewPanel={previewPanel}
        onRetryImport={onRetryImport}
        retryingImportId={retryingImportId}
        retryImportError={retryImportError}
        addQuotes={addQuotes}
        hasInspections={hasInspections}
      />
    )
  }

  const framed = !embedded
  return (
    <div className={framed ? 'rounded-xl border border-border bg-card p-4' : 'max-h-[45vh] overflow-y-auto'}>
      {framed && (
      <button
        type="button"
        onClick={() => setCollapsed(!collapsedNow)}
        aria-expanded={open}
        className="mb-1 flex w-full items-center gap-2 text-left"
      >
        <ClipboardCheck className="h-4 w-4 text-muted-foreground" aria-hidden />
        <p className="text-sm font-semibold text-foreground">Set up QA from quotes</p>
        <ChevronDown
          className={cn(
            'ml-auto h-4 w-4 shrink-0 text-muted-foreground transition-transform',
            open && 'rotate-180',
          )}
          aria-hidden
        />
      </button>
      )}
      {open && (
      <div className={framed ? 'relative overflow-y-auto' : undefined} style={framed ? { maxHeight: maxH } : undefined}>
      {quotesLoading && quotes.length === 0 ? (
        <>
          {addQuotes}
          <p className="text-xs text-muted-foreground mt-2">Loading quotes…</p>
        </>
      ) : quotesError ? (
        <>
          {addQuotes}
          <p className="text-xs text-destructive mt-2">Could not load quotes.</p>
          {onRetryQuotes && (
            <button type="button" onClick={onRetryQuotes} className="mt-1 text-xs font-medium text-primary">
              Retry
            </button>
          )}
        </>
      ) : listedQuotes.length === 0 ? (
        <>
          {addQuotes}
          <p className="text-xs text-muted-foreground mt-2">
            No quotes on this job yet. Add a quote in the Quotes tab and it'll appear here to build QA from.
          </p>
        </>
      ) : (
        <>
          <p className="text-xs text-muted-foreground mb-3">
            Tick the quotes to inspect, or open one to review first.{' '}
            <strong className="font-medium text-foreground">Combine</strong> merges them into one QA list;{' '}
            <strong className="font-medium text-foreground">Separate</strong> creates a QA scope per quote
            (Scope 1, 2…) you can run independently.
          </p>
          {addQuotes}

          {/* Quote multi-select — each row shows the quote's totals, status and
              validity (table-like) and an "Open" jump into the Quote Builder /
              Viewer so the operator can double-check before building QA. The
              row toggles selection; the quote number + Open button open it. */}
          <ul className="space-y-1 mb-3 max-h-72 overflow-y-auto pr-1">
            {listedQuotes.map((q) => {
              const checked = selectedSheetIds.includes(q.id)
              const failed = q.ingest_error ?? null
              const total = Number.parseFloat(q.total_ex_gst)
              const requestToggle = () => {
                if (retryingImportId === q.id) return
                onToggleSheet(q.id)
              }
              return (
                <li key={q.id}>
                  <div
                    onClick={requestToggle}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        requestToggle()
                      }
                    }}
                    aria-pressed={checked}
                    title={
                      failed
                        ? 'This quote failed to import, so there is nothing to build QA from yet'
                        : undefined
                    }
                    className={cn(
                      'flex items-center gap-2.5 px-2.5 py-2 rounded-lg border transition-colors cursor-pointer',
                      checked ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={requestToggle}
                      onClick={(e) => e.stopPropagation()}
                      disabled={retryingImportId === q.id}
                      aria-label={`Select quote ${q.quote_number}`}
                      className="h-4 w-4 shrink-0 rounded border-border text-primary focus:ring-primary"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        {onOpenQuote ? (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onOpenQuote(q) }}
                            className="truncate text-left text-[13px] font-medium text-primary hover:underline"
                            title="Open quote to review"
                          >
                            {q.quote_number}
                          </button>
                        ) : (
                          <span className="truncate text-[13px] font-medium text-foreground">{q.quote_number}</span>
                        )}
                        {q.is_primary && (
                          <span className="shrink-0 rounded-full bg-cat-4-muted px-1.5 py-0.5 text-[9px] font-semibold text-cat-4">
                            Contract
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                        {!failed && (
                          <span className="font-medium text-foreground/80">
                            {Number.isFinite(total) ? formatCurrency(total) : '—'} <span className="font-normal">ex GST</span>
                          </span>
                        )}
                        <StatusBadge status={q.status} />
                        {q.valid_until && <span>Valid {q.valid_until}</span>}
                        {q.deposit_pct ? <span>Deposit {q.deposit_pct}%</span> : null}
                      </div>
                    </div>
                    {onOpenQuote && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onOpenQuote(q) }}
                        className="shrink-0 inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] font-medium text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                        title="Open quote to review"
                      >
                        <ExternalLink className="h-3.5 w-3.5" aria-hidden /> Open
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        void viewQuoteUrl(q, q.job_id ?? 0).then((url) => {
                          const message = url ? openQuotePopup(url) : 'Could not open this quote.'
                          if (message) window.alert(message)
                        })
                      }}
                      className="shrink-0 inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] font-medium text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                    >
                      View
                    </button>
                    {onRemoveQuote && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onRemoveQuote(q.id) }}
                        className="shrink-0 inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] font-medium text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  {failed && (
                    <QuoteIngestErrorNotice
                      error={failed}
                      onRetry={() => onRetryImport?.(q.id)}
                      retrying={retryingImportId === q.id}
                      retryError={retryImportError?.id === q.id ? retryImportError.message : null}
                    />
                  )}
                </li>
              )
            })}
          </ul>

          {/* Classification (industry / NCC class / trades) — AI pre-selected
              from the job, persisted on the generated QA (merges the QA-hub
              classification logic into the job QA setup). */}
          {/* Combine vs separate */}
          <div
            role="radiogroup"
            aria-label="How to create QA from the selected quotes"
            className="inline-flex rounded-lg border border-border p-0.5 mb-3"
          >
            {([
              ['combine', 'Combine into one QA', Layers],
              ['separate', 'Separate QA per quote', SplitSquareHorizontal],
            ] as const).map(([mode, label, Icon]) => (
              <button
                key={mode}
                type="button"
                role="radio"
                aria-checked={createMode === mode}
                onClick={() => {
                  onCreateModeChange(mode)
                  if (selectedSheetIds.length === 0) return
                  if (mode === 'combine' && onCombine) onCombine()
                  else onCreate()
                }}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
                  createMode === mode
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <Icon className="h-3.5 w-3.5" aria-hidden />
                {label}
              </button>
            ))}
          </div>

          {showWriteTarget && (
            <div
              role="radiogroup"
              aria-label="Where this QA goes"
              className="inline-flex rounded-lg border border-border p-0.5 mb-3"
            >
              {([
                ['replace', 'Replace selected'],
                ['create', 'Create new'],
              ] as const).map(([target, label]) => (
                <button
                  key={target}
                  type="button"
                  role="radio"
                  aria-checked={writeTarget === target}
                  onClick={() => onWriteTargetChange?.(target)}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
                    writeTarget === target
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          {onIncludeQuoteDerivedItemsChange && (
            <label className="mb-3 flex items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={includeQuoteDerivedItems}
                onChange={(event) => onIncludeQuoteDerivedItemsChange(event.target.checked)}
              />
              Include quote-derived checks after the default area items
            </label>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              value={scopeName}
              onChange={(e) => onScopeNameChange(e.target.value)}
              placeholder={
                createMode === 'separate' ? 'Scope name (optional, e.g. Level 4)' : 'QA list name (optional)'
              }
              className="flex-1 min-w-[200px] rounded-lg border border-border bg-background px-3 py-2 text-sm"
              aria-label="QA list or scope name"
            />
            <button
              type="button"
              onClick={onCreate}
              disabled={selectedSheetIds.length === 0 || createPending}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {createPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : createMode === 'combine' ? (
                <Layers className="h-4 w-4" aria-hidden />
              ) : (
                <SplitSquareHorizontal className="h-4 w-4" aria-hidden />
              )}
              {createPending && createProgress
                ? `Creating scope ${createProgress.current} of ${createProgress.total}…`
                : `Create QA${selectedSheetIds.length > 1 ? ` from ${selectedSheetIds.length} quotes` : ''}`}
            </button>
          </div>
          {createNotice ? (
            <p role="status" className="mt-2 text-xs text-amber-700 dark:text-amber-300">{createNotice}</p>
          ) : null}
          {previewPanel}
        </>
      )}
      {framed && (
      <ResizeHandle
        axis="y"
        sign={1}
        value={maxH}
        min={160}
        max={720}
        onChange={setMaxH}
        resetTo={460}
        ariaLabel="Resize setup card height"
      />
      )}
      </div>
      )}
      {genError && (
        <div className="mt-2 flex items-start gap-2 text-xs text-destructive">
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden />
          <span>{genError}</span>
        </div>
      )}
    </div>
  )
}
